/**
 * Auth strategy resolver.
 *
 * Decides at runtime whether to use the Cognito-backed auth service or the
 * legacy Rails-backed client-app service.  The decision is driven by the
 * `useCognitoAuth` flag and the completeness of the Cognito configuration.
 *
 * When Cognito is requested but required config is missing the resolver
 * logs an error and falls back to the legacy service — the app never
 * crashes due to missing auth environment variables.
 *
 * @module
 */
import { dasherizeKeys } from '@zoocasa/node-kit/objects';
import { camelize } from '@zoocasa/node-kit/strings/camelize';
import { ClientAppAuthService } from './client-app/index';
import { CognitoAuthService } from './cognito/index';
import { getMissingCognitoConfig, type CognitoConfig } from './cognito-config';
import { configureAmplifyAuth, type MergeAmplifyConfigFn } from './amplify/config';
import type { HttpClient } from './http';
import type { AuthService, AuthObservabilityService, SessionPersistence, SocialAuthListener, TranslationPort } from '../types';
import type { AuthLogger } from '../shared/auth-logger';

const KEY_TRANSFORMS = {
  dasherize: dasherizeKeys,
  camelize,
};

/** Feature-flag and configuration inputs that drive strategy selection. */
interface AuthStrategyOptions {
  /** Whether the `use-cognito-auth` GrowthBook flag is enabled. */
  useCognitoAuth: boolean;
  /** Current tenant theme name (e.g. `'zoocasa'`, `'exp-realty-ca'`). */
  themeName: string;
  /** i18n translation port for localised error messages. */
  translation: TranslationPort;
  /** Cognito pool / domain configuration.  Required when {@link useCognitoAuth} is `true`. */
  cognitoConfig?: CognitoConfig;
  /** Amplify SDK config-merge callback.  Required when {@link useCognitoAuth} is `true`. */
  mergeAmplifyConfig?: MergeAmplifyConfigFn;
}

/** Pre-constructed adapters that the resolver wires into the chosen service. */
interface AuthStrategyDeps {
  /** The pair of HTTP clients built by {@link buildHttpClients}. */
  clients: { httpClient: HttpClient; cognitoHttpClient: HttpClient };
  /** Structured logger scoped to `[auth]`. */
  logger: AuthLogger;
  /** Cookie-backed session persistence (needed by the legacy service reader). */
  persistence: SessionPersistence;
  /** Optional observability tracker forwarded to the chosen service. */
  observability?: AuthObservabilityService;
  /** Required when Cognito is enabled — used as the Amplify Hub listener. */
  socialAuthListener?: SocialAuthListener;
}

/** Value returned by {@link resolveAuthService}. */
interface ResolvedAuthService {
  /** The concrete `AuthService` implementation to use for this session. */
  authService: AuthService;
  /** `true` when Amplify was successfully configured for Cognito OAuth. */
  amplifyConfigured: boolean;
}

/**
 * Selects and instantiates the correct {@link AuthService} implementation.
 *
 * When `options.useCognitoAuth` is `true` but the required Cognito
 * configuration is incomplete, the resolver logs an error to the console
 * and silently falls back to the legacy `ClientAppAuthService`.
 */
export function resolveAuthService(options: AuthStrategyOptions, deps: AuthStrategyDeps): ResolvedAuthService {
  let useCognito = options.useCognitoAuth;

  if (useCognito) {
    const missing = getMissingCognitoConfig(options.cognitoConfig);
    if (!options.mergeAmplifyConfig) missing.push('mergeAmplifyConfig');
    if (missing.length > 0) {
      console.error(
        `[auth] useCognitoAuth is ON but required config is missing: ${missing.join(', ')}. ` +
        'Falling back to legacy auth. Add the missing env vars and rebuild.',
      );
      useCognito = false;
    }
  }

  const amplifyConfigured = useCognito && deps.socialAuthListener && options.cognitoConfig && options.mergeAmplifyConfig
    ? configureAmplifyAuth(options.cognitoConfig, options.mergeAmplifyConfig, deps.socialAuthListener)
    : false;

  const authService = useCognito
    ? new CognitoAuthService({
      http: deps.clients.cognitoHttpClient,
      clientAppHttp: deps.clients.httpClient,
      translation: options.translation,
      logger: deps.logger,
      cognitoConfig: options.cognitoConfig!,
      amplifyConfigured,
      observability: deps.observability,
      tenant: options.themeName,
    })
    : new ClientAppAuthService({
      http: deps.clients.httpClient,
      keys: KEY_TRANSFORMS,
      translation: options.translation,
      themeName: options.themeName,
      sessionReader: { loadUser: () => deps.persistence.loadUser() },
      observability: deps.observability,
    });

  return { authService, amplifyConfigured };
}
